<template>
  <div class="car-selection-container">
    <div class="car-selection-wrapper" v-if="data && (data.bookingSettings?.groups || data.visibilitySettings?.groups)">
      <CCarCategory
        @change="value => $emit('change', value)"
        v-for="(item) in carMappedData as DriveGroup[]"
        :key="`g-${item.a_id}`"
        :data="item"
        :visibility="data.visibilitySettings"
        :selectable="data.bookingSettings"
        :value="props.value"
      />
    </div>
    <div class="car-selection-wrapper" v-else-if="data && data.visibilitySettings && (data.bookingSettings?.classes || data.visibilitySettings?.classes)">
      <CCarSubcategory
        hasBorder
        @change="value => $emit('change', value)"
        v-for="(item) in carMappedData as DriveClass[]"
        :key="`c-${item.a_id}`"
        :data="item"
        :visibility="data.visibilitySettings"
        :selectable="data.bookingSettings"
        :value="props.value"
      />
    </div>
    <div v-else-if="data && data.visibilitySettings && (data.bookingSettings?.assets || data.visibilitySettings?.assets)">
      <CCarSelectionOption
        @change="value => $emit('change', value)"
        :selectable="data.bookingSettings"
        v-for="(item) in carMappedData as DriveAsset[]"
        :key="`a-${item.a_id}`"
        :checked="props.value === item.a_id"
        :valueId="item.a_id"
        :title="item.brand.name"
        :details="item.description"
        :licensePlate="item.registrationNumber"
        :value="props.value"
        :logo="item.brand.logoUrl"
      />
    </div>
    <CCarManualSelection
      v-if="!props.hideManualEntry"
      @change="value => $emit('change', value)"
      @changeLicensePlate="handleLicensePlateChange"
      @changeModel="handleModelChange"
      :checked="props.value === DriveReservationRequestType.MANUAL"
      :licensePlate="props.manualLicensePlate"
      :carModel="props.manualCarModel"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import CCarCategory from './CCarCategory.vue';
  import CCarSelectionOption from './CCarSelectionOption.vue';
  import CCarSubcategory from './CCarSubcategory.vue';
  import CCarManualSelection from './CCarManualSelection.vue';
  import DriveReservationRequestType from '@/driveBookings/constants/DriveReservationRequestType';
  import DriveAvailableAssets from '../models/DriveAvailableAssets';
  import { DriveAsset, DriveClass, DriveGroup, DriveNestedData, fromReceivedDriveReservationToDriveReservationAssetsSelection } from '../utils/driveReservationCreationMapper';

  const props = defineProps<{
    data: DriveAvailableAssets | null,
    value: string,
    manualLicensePlate?: string,
    manualCarModel?: string,
    selectedList?: {group: string | null, class: string | null, asset: string | null},
    hideManualEntry?: boolean,
  }>();

  const licensePlate = ref('');
  const carModel = ref('');
  const carMappedData = ref<DriveNestedData>([]);

  const emit = defineEmits<{
    (e: 'change', v: string): void,
    (e: 'licensePlateChange', v: string): void,
    (e: 'modelChange', v: string): void,
  }>();

  function handleLicensePlateChange(value: string) {
    licensePlate.value = value;

    emit('licensePlateChange', value);
  }

  function handleModelChange(value: string) {
    carModel.value = value;

    emit('modelChange', value);
  }

  watch(() => props.data, (newData) => {
    if (newData) {
      carMappedData.value = fromReceivedDriveReservationToDriveReservationAssetsSelection(newData);
    }
  }, { immediate: true });

</script>

<style lang="scss" scoped>
.car-selection-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.car-selection-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.car-selection-container > div > .option-wrapper:not(:last-child) {
  margin-bottom: 12px;
}

.car-selection-container > div > .subcategory-wrapper::before {
  display: none;
}

</style>